// Shared types + design tokens for the play-centric dashboard / profile.
//
// The dashboard's live data now comes from `use-play-dashboard-data.ts`
// (teams, schedule, assignments, stats via real stores/APIs). This module keeps
// only the shape definitions and Figma design tokens consumed across the play
// surface (widgets, LeftSidebar, PlayTopbar). No mock/sample data lives here.
//
// Filename kept as `mock-data` to avoid churning the ~20 import sites; it is a
// types/tokens module in practice.

// PlayProfileRole is shared at src/constants/play-profile so non-page consumers
// (LeftSidebar, layout) can import it without a reverse dependency. Re-exported
// here for back-compat with existing call sites.
import type { PlayProfileRole } from 'constants/play-profile'
export type { PlayProfileRole }

export interface PlayProfileSummary {
  id: string
  name: string
  role: PlayProfileRole
  avatarColor: string
  initials: string
  subline?: string
  /** Uploaded profile photo URL; when present the avatar renders it instead of initials. */
  avatarUrl?: string
}

export interface DrillItem {
  id: string
  title: string
  durationMin: number
  reps?: number
  category: 'Hitting' | 'Defense' | 'Throw' | 'Speed'
  done: boolean
}

export interface RallyDayPlan {
  streakDays: number
  weekDots: { label: string; active: boolean }[]
  nextUpTime: string
  routineTitle: string
  routineSubtitle: string
  routineDurationMin: number
  routineReps: number
  equipment: string
  drillCount: number
  todayDrills: DrillItem[]
}

export interface AssignedPlan {
  id: string
  title: string
  org: string
  weekProgress: number
  weekTotal: number
  status: 'on-track' | 'behind' | 'ahead'
}

export interface PlayerOrg {
  id: string
  short: string
  name: string
  role: string
  color: string
}

export interface TeamInfo {
  name: string
  coach: string
  level: string
  nextEvent: string
  nextEventTime: string
  color: string
  initials: string
}

export interface PlayerNotification {
  id: string
  type: 'invite' | 'reminder' | 'message'
  title: string
  body: string
  timeAgo: string
  unread: boolean
}

export interface QuickStat {
  id: string
  value: string
  label: string
  delta: string
  iconKey: 'training' | 'teams' | 'completion' | 'upcoming'
  accent: string
  /** No backend source yet — render dimmed/muted with a "coming soon" tooltip. */
  tbd?: boolean
}

export interface TeamFilter {
  id: string
  name: string
  color: string
}

export interface WorkoutItem {
  id: string
  title: string
  startTime: string
  endTime: string
  location: string
  orgId: string
  // ISO date 'YYYY-MM-DD' for filtering by day chip
  date: string
}

export interface TodoItem {
  id: string
  icon: string
  title: string
  teamTag: { label: string; color: string }
  due: string
  completed: boolean
}

export type NextUpAccent = 'blue' | 'orange' | 'green'

export interface NextUpCardData {
  id: string
  tag: string
  title: string
  byline: string
  meta: string[]
  ctaLabel: string
  accent: NextUpAccent
}

export interface CalendarEvent {
  id: string
  /**
   * ISO date, 'YYYY-MM-DD'. A weekday index cannot address a day outside the
   * current week, which is what the calendar's Week and Month navigation moves
   * through, so the event carries the date itself.
   */
  date: string
  /** 'HH:mm', 24-hour. */
  start: string
  end: string
  title: string
  location: string
  /** Org id (string) that owns this event — used by the team filter chip. */
  orgId: string
  teamTag: { label: string; color: string }
}

// Badge color tokens — exported so headers can swap player vs youth styling
// without re-declaring the same hex literals.
export const PLAYER_BADGE_BG = 'rgba(77,179,255,0.18)'
export const PLAYER_BADGE_COLOR = '#4DB3FF'
export const YOUTH_BADGE_BG = 'rgba(245,158,11,0.18)'
export const YOUTH_BADGE_COLOR = '#F59E0B'

// Surface tokens for the play dashboard desktop shell. Body uses a flat bg that
// matches mobile for consistent contrast against the elevated cards.
export const PLAY_DASHBOARD_BODY_BG = '#0a1322'
// Card / surface bg used on top of the body bg (StreakBanner).
export const PLAY_SURFACE_BG = '#172437'
// Surface Gradient stops — kept for consumers that need the original gradient
// (e.g. PlayTopbar bg seam against the body).
export const PLAY_DASHBOARD_BG_GRADIENT_FROM = '#172437'
export const PLAY_DASHBOARD_BG_GRADIENT_TO = '#0E1928'
export const PLAY_FILTER_BG = 'rgba(255,255,255,0.03)'
export const PLAY_FILTER_BORDER = 'rgba(194,199,214,0.12)'

// Topbar V2 (PlayTopbar) tokens — per Figma component spec.
export const PLAY_TOPBAR_HEIGHT = '48px'
export const PLAY_TOPBAR_BUTTON_BG = 'rgba(255,255,255,0.08)' // WhiteAlpha/200
export const PLAY_TOPBAR_BUTTON_BORDER = '#586981' // Grey stroke
export const PLAY_TOPBAR_INPUT_BG = 'rgba(255,255,255,0.03)'
export const PLAY_TOPBAR_INPUT_BORDER = 'rgba(194,199,214,0.12)'
export const PLAY_TOPBAR_PROFILE_BORDER = 'rgba(194,199,214,0.12)' // Surface Stroke (#C2C7D6 12%)

// Per-role profile overview shape.
export interface DashboardData {
  greetingName: string
  rallyDay: RallyDayPlan
  assignedPlans: AssignedPlan[]
  orgs: PlayerOrg[]
  team: TeamInfo
  notifications: PlayerNotification[]
  quickStats: QuickStat[]
  teamFilters: TeamFilter[]
  workouts: WorkoutItem[]
  todos: TodoItem[]
  nextUpCards: NextUpCardData[]
  calendarEvents: CalendarEvent[]
}

export interface PlayerProfileFields {
  overall: number
  positions: string[]
  primary: string
  bats: 'L' | 'R' | 'S'
  throws: 'L' | 'R'
  class: number
  dob: string
  height: string
  weight: string
  ageGroup: string
  level: string
  verified: boolean
  completeness: number
  developmentScore: number
}

// Brand-colored team palette for the player view. Used by the team-filter pills
// and the colored border on each workout row to wayfind which team owns a
// session. Team accent colors matched to Figma (Yellow/500, Sky/500, Orange/500).
export const TEAM_ALL_COLOR = '#FFA500' // Yellow/500
export const TEAM_THUNDER_COLOR = '#0EA5E9' // Sky/500
export const TEAM_ELITE_COLOR = '#F97316' // Orange/500
export const FILTER_LABEL_COLOR = '#6B7280' // Gray/500 — "Filter:" label
